import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";

const Context = createContext();

export function useMember() {
  return useContext(Context);
}

const initState = {
  list: [],
  listCars: [],
  listPayments: [],
  selected: null,
  memberQRField: {
    site: false,
    plate: false,
    mobile: false,
    expireDate: false,
    relatedCompany: false,
    text: null,
    checkedText:false,
    BlackonWhite: "black-on-white",
  },
  count: 1,
  page: 1,
  perPage: 10,
  length: 0,
  companyIdAtt: 0,
  planIdAtt: 0,
  status: "idle",
  error: null,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };

    case "set_listCars":
      return { ...state, listCars: [...action.payload] };
    case "set_listPayments":
      return { ...state, listPayments: [...action.payload] };
    case "set_selected":
      return { ...state, selected: { ...action.payload } };
    case "set_memberQRFields":
      return { ...state, memberQRField: { ...action.payload } };
    case "set_count":
      return { ...state, count: action.payload };
    case "set_companyIdAtt":
      return { ...state, companyIdAtt: action.payload };
    case "set_planIdAtt":
      return { ...state, planIdAtt: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "edit":
      const modified = state.list.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p,
      );
      return { ...state, list: modified };
    case "delete":
      const shouldPermanentlyDelete = action.payload.isDelete;
      const targetId = action.payload.id;

      const updatedList = shouldPermanentlyDelete
        ? state.list.filter((item) => item.id !== targetId)
        : state.list.map((item) =>
            item.id === targetId ? { ...item, isActive: false } : item,
          );

      return { ...state, list: updatedList };
    case "addCar":
      const updatedCars = [{ ...action.payload }, ...state.selected.cars];
      return {
        ...state,
        selected: {
          ...state.list,
          cars: updatedCars,
        },
      };
    case "editCar":
      const editCars = state.selected?.cars.map((car) =>
        car.id === action.payload.id ? { ...car, ...action.payload } : car,
      );
      return {
        ...state,
        selected: {
          ...state.selected,
          cars: editCars,
        },
      };

    case "deleteCar":
      const filteredCar = state.selected.cars.filter(
        (p) => p.id !== action.payload,
      );
      return {
        ...state,
        selected: {
          ...state.selected,
          cars: filteredCar,
        },
      };
    case "status":
      return { ...state, status: action.payload };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };
    case "set_length":
      return { ...state, length: action.payload };
    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function MemberProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();
  const fetchList = useCallback(
    async (cid, pid, data, page, perPage) => {
      dispatch({ type: "status", payload: "fetching" });

      let headersData = {
        pageNumber: page,
      };

      if (perPage) headersData.pageSize = perPage;
      if (data.status || data.status === false)
        headersData.status = data.status;
      if (data.search) headersData.search = data.search;
      if (data.siteId) headersData.siteId = data.siteId;

      const resData = await req(
        `CompanyMember/List/${cid}, ${pid}`,
        null,
        {},
        true,
        headersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData.data.members || [] });
      dispatch({ type: "set_per_page", payload: resData.data.pageSize || 0 });
      dispatch({ type: "set_count", payload: resData.data.totalPages || 0 });
      dispatch({ type: "set_length", payload: resData.data.totalSize || 0 });
    },
    [req],
  );

  const fetchOne = useCallback(
    (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        try {
          const resData = await req(`CompanyMember/Info/${id}`, null, {}, true);
          const memberData = resData.data;
          dispatch({ type: "set_selected", payload: memberData });
          if (memberData?.printingInfo) {
            try {
              const parsed = JSON.parse(memberData.printingInfo);
              dispatch({
                type: "set_memberQRFields",
                payload: {
                  ...initState.memberQRField,
                  ...parsed,
                },
              });
            } catch (e) {
              console.error("Failed to parse printingInfo", e);
            }
          } else {
            dispatch({ type: "set_memberQRFields", payload: initState.memberQRField });
          }
          dispatch({ type: "status", payload: `idle` });
          resolve(resData);
        } catch (error) {
          reject(error);
        }
      });
    },
    [req],
  );

  const create = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        const newData = { ...data };
        try {
          const resData = await req(
            "CompanyMember/Add",
            newData,
            { method: "POST" },
            true,
          );
          dispatch({ type: "add", payload: resData.data });
          dispatch({ type: "status", payload: `idle` });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
  const edit = useCallback(
    async (data, id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `CompanyMember/Update/${id}`,
            data,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "edit", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
  const remove = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          dispatch({ type: "status", payload: `deleting ${id}` });
          const resData = await req(
            `CompanyMember/Delete/${id}/`,
            {},
            { method: "DELETE" },
            true,
          );
          dispatch({ type: "status", payload: "idle" });
          dispatch({
            type: "delete",
            payload: { id: id, isDelete: resData.data },
          });
          resolve(resData);
        } catch (error) {
          reject(error);
        }
      });
    },
    [req],
  );

  const createCar = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        const newData = { ...data };
        try {
          const resData = await req(
            "CompanyMember/AddCar",
            newData,
            { method: "POST" },
            true,
          );
          dispatch({ type: "addCar", payload: resData.data });
          dispatch({ type: "status", payload: `idle` });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
  const editCar = useCallback(
    async (data, id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `CompanyMember/UpdateCar/${id}`,
            data,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "editCar", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
  const removeCar = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          dispatch({ type: "status", payload: `deleting ${id}` });
          const resData = await req(
            `CompanyMember/DeleteCar/${id}`,
            {},
            { method: "DELETE" },
            true,
          );
          dispatch({ type: "status", payload: "idle" });
          dispatch({ type: "deleteCar", payload: id });
          resolve(resData);
        } catch (error) {
          reject(error);
        }
      });
    },
    [req],
  );

  const fetchListCars = useCallback(
    async (mid) => {
      dispatch({ type: "status", payload: "fetching" });

      const resData = await req(`CompanyMember/CarList/${mid}`, null, {}, true);
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_listCars", payload: resData.data || [] });
    },
    [req],
  );

  const fetchPayments = useCallback(
    async (memberId, data, page, perPage) => {
      dispatch({ type: "status", payload: "fetching" });
      let headersData = {
        pageNumber: page,
      };
      if (perPage) headersData.pageSize = perPage;
      if (data.from) headersData.from = data.from;
      if (data.to) headersData.to = data.to;

      const resData = await req(
        `CompanyMember/PaymentList/${memberId}`,
        null,
        {},
        true,
        headersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({
        type: "set_listPayments",
        payload: resData.data.payments || [],
      });
      // dispatch({ type: "set_per_page", payload: resData.data.pageSize || 0 });
      // dispatch({ type: "set_count", payload: resData.data.totalPages || 0 });
      // dispatch({ type: "set_length", payload: resData.data.totalSize || 0 });
    },
    [req],
  );

  const ChargeAPI = useCallback(
    async (data, id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `CompanyMember/Charge/${id}`,
            data,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "edit", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );
   const uploadExcelFile = useCallback(
      async (data,companyId, planId) => {
        let formData = new FormData();
        formData.append("file", data);
        let headerFileData = {};
        if (planId) headerFileData.planId = planId;
        if (companyId) headerFileData.companyId = companyId;
        return new Promise(async (resolve, reject) => {
          try {
            const resData = await req(
              "CompanyMember/Import",
              formData,
              { method: "POST", headers: headerFileData },
              true,
            );
          //  dispatch({ type: "set_File", payload: resData.data.fileName });
            resolve(resData.data);
          } catch (error) {
            console.log(error);
            reject(error);
          }
        });
      },
      [req],
    );
  return (
    <Context.Provider
      value={{
        state,
        dispatch,
        fetchList,
        fetchOne,
        edit,
        create,
        remove,
        createCar,
        editCar,
        removeCar,
        fetchListCars,
        fetchPayments,
        ChargeAPI,
        uploadExcelFile,
      }}
    >
      {children}
    </Context.Provider>
  );
}
